import React from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Box } from "@mui/material";

interface RevenueOverview {
    week: string;
    revenue: number;
}

interface AreaChartProps {
    revenueOverview: RevenueOverview[];
}

const AreaChart: React.FC<AreaChartProps> = ({ revenueOverview }) => {
    const categories = revenueOverview.map((item) => item.week);
    const revenueData = revenueOverview.map((item) => item.revenue);

    const maxRevenue = Math.max(...revenueData, 0);

    // Give the chart some extra space above the highest value
    const yAxisMax = Math.ceil(maxRevenue / 5000) * 5000 + 5000;

    const options: ApexOptions = {
        chart: {
            type: "area",
            toolbar: {
                show: false,
            },
            zoom: {
                enabled: false,
            },
            fontFamily: "Inter, sans-serif",
        },

        stroke: {
            curve: "smooth",
            width: 3,
            colors: ["var(--primary-color)"],
        },

        markers: {
            size: 4,
            strokeWidth: 2,
            strokeColors: "var(--primary-color)",
            colors: ["#fff"],
            hover: {
                size: 6,
            },
        },

        dataLabels: {
            enabled: false,
        },

        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 0,
                opacityFrom: 0.3,
                opacityTo: 0.05,
                stops: [0, 100],
            },
            colors: ["var(--primary-color)"],
        },

        grid: {
            borderColor: "#EAEAEA",
            strokeDashArray: 4,
            xaxis: {
                lines: {
                    show: false,
                },
            },
            yaxis: {
                lines: {
                    show: true,
                },
            },
            padding: {
                left: 10,
                right: 10,
            },
        },

        xaxis: {
            categories,
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            labels: {
                style: {
                    colors: "#8A8A8A",
                    fontSize: "13px",
                },
            },
        },

        yaxis: {
            min: 0,
            max: yAxisMax,
            tickAmount: 5,
            labels: {
                formatter: (value) => {
                    if (value === 0) return "$0";

                    return `$${(value / 1000).toFixed(0)}K`;
                },
                style: {
                    colors: "#8A8A8A",
                    fontSize: "13px",
                },
            },
        },

        tooltip: {
            y: {
                formatter: (value) =>
                    `$${value.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}`,
            },
        },

        legend: {
            show: false,
        },
    };

    const series = [
        {
            name: "Revenue",
            data: revenueData,
        },
    ];

    return (
        <Box className="w-100">
            <Chart
                options={options}
                series={series}
                type="area"
                height={300}
                width="100%"
            />
        </Box>
    );
};

export default AreaChart;